<script lang="ts" setup>
import {
  User,
  UserLevel,
  firstnameRules,
  lastnameRules,
  levelRules,
} from '@/models/user.model';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { UsersActions, UsersState } from '@/stores/users.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import UserStories from './UserStories.vue';
import UserBPT from './UserBPT.vue';
import UserLabels from './UserLabels.vue';
import UserNotifications from './UserNotifications.vue';
import UserMessages from './UserMessages.vue';
import UserGoals from './UserGoals.vue';
import UserActions from './UserActions.vue';
import UserInitiatives from './UserInitiatives.vue';
import UserInfoGraphics from './UserInfoGraphics.vue';
import UserCo2Quiz from '@/components/users/UserCo2Quiz.vue';
import UserCommunity from './UserCommunity.vue';

const store = useStore<ShiftCMSState & { cs: UsersState }>();

const props = defineProps({
  data: {
    type: Object as PropType<User>,
  },
});

const storeUser = computed<User | null>(() => store.state.cs.user);

const valid = ref<boolean>(false);

const user = reactive<User>({
  id: NEW_ID,
  firstName: '',
  lastName: '',
  deviceID: '',
  email: '',
  language: 'nl',
  level: UserLevel.Device,
  actions: [],
  labels: [],
  steps: [],
  stories: [],
  userCo2QuizAnswers: [],
  infographics: [],
  initiatives: [],
  notificationSettings: [],
  communities: [],
});

watch(
  () => storeUser,
  (v) => {
    Object.assign(user, v);
  },
);

const showResetPassword = ref<boolean>(false);
const password = ref<string>('');

function resetPassword() {
  showResetPassword.value = true;
}

async function saveResetPassword() {
  if (user && user.id !== -1) {
    await store.dispatch(UsersActions.ResetPassword, {
      id: user.id,
      password: password.value,
    });
    showResetPassword.value = false;
  }
}

const action = computed<string>(() => (user.id === NEW_ID ? 'Add' : 'Save'));

const saveUser = async () => {
  let res;
  if (valid.value) {
    if (user.id === -1) {
      res = await store.dispatch(UsersActions.AddUser, user);
    } else {
      res = await store.dispatch(UsersActions.UpdateUser, user);
    }
  }
  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved user',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetUsers);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: res.message,
      type: 'warning',
    });
  }
};

const deleteUser = async () => {
  if (confirm('Are you sure you want to delete this user and all their data')) {
    await store.dispatch(UsersActions.DeleteUser, user);
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetUserList, {
      search: '',
      level: null,
      page: 1,
      count: 25,
      sort: 'id',
      direction: 'asc',
    });
  }
};

const resetAll = async () => {
  if (confirm('Are you sure you want to reset all data for this user')) {
    await store.dispatch(UsersActions.ResetUser, user);
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetUserList, {
      search: '',
      level: null,
      page: 1,
      count: 25,
      sort: 'id',
      direction: 'asc',
    });
  }
};

const resetBPT = async () => {
  if (user.id !== NEW_ID) {
    const u = await store.dispatch(UsersActions.GetFullUser, user.id);

    Object.assign(user, u);
  }
};

const disable2FA = async () => {
  const result = await store.dispatch(UsersActions.Disable2FA, user.id);

  if (result.twoFactorDisabled) {
    const r = store.dispatch(UsersActions.GetUser, user.id);
    Object.assign(user, r);
    store.dispatch(Actions.ShowNotification, {
      message: result.message,
      type: 'success',
    });
  }
};

onMounted(() => {
  if (props.data) {
    Object.assign(user, props.data);

    if (user.id !== NEW_ID) {
      store.dispatch(UsersActions.GetFullUser, user.id);
    }
  }
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveUser">
    <v-container>
      <h2>{{ $t(action) }} a user</h2>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="user.firstName"
            :rules="firstnameRules"
            label="First name"
            hide-details
            required
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto">
          <v-text-field
            v-model="user.lastName"
            :rules="lastnameRules"
            label="Last name"
            hide-details
            required
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="user.email"
            label="Email"
            required
          ></v-text-field>
        </v-col>
        <v-col class="mx-auto">
          <v-select
            v-model="user.level"
            :rules="levelRules"
            :items="[
              { title: 'Device Registration', value: UserLevel.Device },
              { title: 'User Registration', value: UserLevel.RegisteredUser },
              { title: 'Content Editor', value: UserLevel.ContentEditor },
              { title: 'Admin', value: UserLevel.Admin },
            ]"
            label="Level"
            required
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col col="2"> </v-col>
        <v-col v-if="user.id !== -1">
          <v-btn class="mt-2" color="warning" @click="deleteUser" block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" color="warning" block @click="resetAll"
            >Reset All</v-btn
          >
        </v-col>
        <v-col v-if="user.id !== -1">
          <v-btn class="mt-2" type="button" @click="resetPassword" block
            >Reset Password</v-btn
          >
        </v-col>
        <v-col v-if="user.id !== -1 && user.twoFactorEnabled">
          <v-btn
            class="mt-2"
            color="warning"
            type="button"
            block
            @click="disable2FA"
            >Disable 2FA</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" color="success" type="submit" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2">
          <v-chip
            class="mt-3"
            variant="flat"
            color="info"
            v-if="user.level === UserLevel.Admin && user.twoFactorEnabled"
            >2FA</v-chip
          >
          <v-chip
            class="mt-3"
            variant="flat"
            color="warning"
            v-if="user.level === UserLevel.Admin && !user.twoFactorEnabled"
            >No 2FA</v-chip
          >
        </v-col>
      </v-row>
    </v-container>
  </v-form>
  <v-form v-if="showResetPassword">
    <v-container>
      <p>Provide a new password for the user, and press "reset":</p>
      <v-row>
        <v-col cols="9">
          <v-text-field
            v-model="password"
            label="New Password"
            required
          ></v-text-field>
        </v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            :disabled="!password.length"
            @click="saveResetPassword"
            block
            >Reset</v-btn
          >
        </v-col>
      </v-row>
    </v-container>
  </v-form>
  <v-container v-if="user.id !== -1">
    <UserLabels :user="data" v-if="data" />
    <UserCommunity :user="data" v-if="data" />
    <UserNotifications :user="user" v-if="user" />
    <UserMessages :user="user" v-if="user" />
    <UserGoals :user="user" v-if="user" />
    <UserBPT :user="user" v-if="user" @reset="resetBPT" />
    <UserStories :user="user" />
    <UserActions :user="user" />
    <UserInitiatives :user="user" />
    <UserInfoGraphics :user="user" />
    <UserCo2Quiz :user="data" v-if="data" />
  </v-container>
</template>
